/**
 * System config reader using React Relay.
 *
 * Fetches one system by gid and exposes its `config` Json scalar, which the
 * server returns already parsed (an object, not an encoded string). Used by
 * Settings > Fleet, which reads and writes the same JSON through
 * `patchSystemConfig`, so the value shown always matches what the mutation
 * merges into.
 *
 * Note: Run `npm run relay` after editing the GraphQL query.
 */
import { fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSystemConfigRelayQuery as UseSystemConfigRelayQuery } from './__generated__/useSystemConfigRelayQuery.graphql';

const SystemConfigQuery = graphql`
  query useSystemConfigRelayQuery($id: ID!) {
    getSystem(id: $id) {
      id
      config
    }
  }
`;

export type SystemConfig = Record<string, unknown>;

export function useSystemConfigRelay(systemId: string | undefined) {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(systemId));
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    if (!systemId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);

    fetchQuery<UseSystemConfigRelayQuery>(relayEnvironment, SystemConfigQuery, {
      id: systemId,
    })
      .toPromise()
      .then((result: UseSystemConfigRelayQuery['response'] | undefined) => {
        if (cancelled) return;
        setConfig((result?.getSystem?.config as SystemConfig | null | undefined) ?? null);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const errorObj =
          err instanceof Error ? err : new Error('Failed to fetch system config');
        setError(errorObj);
        console.error('[useSystemConfigRelay] fetch error:', errorObj);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [systemId, fetchCount]);

  const refetch = useCallback(() => setFetchCount((c) => c + 1), []);

  return useMemo(
    () => ({ config, isLoading, error, refetch }),
    [config, isLoading, error, refetch]
  );
}
